import { 
  CognitoUserPool, 
  CognitoUser, 
  AuthenticationDetails, 
  CognitoUserSession,
  CognitoIdToken,
  CognitoAccessToken,
  CognitoRefreshToken
} from 'amazon-cognito-identity-js';
import AWS from 'aws-sdk';
import { config } from '../config';

// Initialize Cognito User Pool
const userPool = new CognitoUserPool({
  UserPoolId: config.aws.userPoolId!,
  ClientId: config.aws.clientId!,
});

// Types for authentication
export interface AuthUser {
  username: string;
  email?: string;
  sub: string;
  idToken: string;
  accessToken: string;
  refreshToken: string;
  groups: string[];
  isAdmin: boolean;
}

export interface SignInCredentials {
  username: string;
  password: string;
}

class AuthService {
  private currentUser: CognitoUser | null = null;
  private userSession: CognitoUserSession | null = null;

  /**
   * Sign in with username and password
   */
  async signIn(credentials: SignInCredentials): Promise<AuthUser> {
    return new Promise((resolve, reject) => {
      const { username, password } = credentials;
      
      const authenticationDetails = new AuthenticationDetails({
        Username: username,
        Password: password,
      });

      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool,
      });

      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: (session: CognitoUserSession) => {
          this.currentUser = cognitoUser;
          this.userSession = session;
          
          // Update AWS credentials with the new tokens
          this.updateAWSCredentials(session);
          
          const idToken = session.getIdToken().getJwtToken();
          const accessToken = session.getAccessToken().getJwtToken();
          
          // // Temporary logging for debugging
          // console.log('🔑 ID Token:', idToken);
          // console.log('🔑 Access Token:', accessToken);
          
          const user: AuthUser = {
            username: session.getIdToken().payload['cognito:username'],
            email: session.getIdToken().payload.email,
            sub: session.getIdToken().payload.sub,
            idToken: session.getIdToken().getJwtToken(),
            accessToken: session.getAccessToken().getJwtToken(),
            refreshToken: session.getRefreshToken().getToken(),
            groups: session.getIdToken().payload['cognito:groups'] || [],
            isAdmin: session.getIdToken().payload['cognito:groups']?.includes('admin') || false,
          };
          
          resolve(user);
        },
        onFailure: (error) => {
          // console.error('Authentication failed:', error);
          reject(error);
        },
        newPasswordRequired: (userAttributes, requiredAttributes) => {
          // Handle new password required case
          reject(new Error('New password required. Please implement password change flow.'));
        },
      });
    });
  }

  /**
   * Sign out current user
   */
  async signOut(): Promise<void> {
    if (this.currentUser) {
      this.currentUser.signOut();
      this.currentUser = null;
      this.userSession = null;
      
      // Clear AWS credentials
      AWS.config.credentials = null;
    }
  }

  /**
   * Get current authenticated user
   */
  async getCurrentUser(): Promise<AuthUser | null> {
    return new Promise((resolve) => {
      const cognitoUser = userPool.getCurrentUser();
      
      if (!cognitoUser) {
        resolve(null);
        return;
      }

      cognitoUser.getSession((error: any, session: CognitoUserSession) => {
        if (error || !session.isValid()) {
          resolve(null);
          return;
        }

        this.currentUser = cognitoUser;
        this.userSession = session;
        
        // Update AWS credentials
        this.updateAWSCredentials(session);

        const user: AuthUser = {
          username: session.getIdToken().payload['cognito:username'],
          email: session.getIdToken().payload.email,
          sub: session.getIdToken().payload.sub,
          idToken: session.getIdToken().getJwtToken(),
          accessToken: session.getAccessToken().getJwtToken(),
          refreshToken: session.getRefreshToken().getToken(),
          groups: session.getIdToken().payload['cognito:groups'] || [],
          isAdmin: session.getIdToken().payload['cognito:groups']?.includes('admin') || false,
        };

        resolve(user);
      });
    });
  }

  /**
   * Get current session
   */
  async getCurrentSession(): Promise<CognitoUserSession | null> {
    if (this.userSession && this.userSession.isValid()) {
      return this.userSession;
    }

    // Try to get fresh session
    await this.getCurrentUser();
    return this.userSession;
  }

  /**
   * Check if user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    const user = await this.getCurrentUser();
    return user !== null;
  }

  /**
   * Update AWS credentials with Cognito tokens
   */
  private updateAWSCredentials(session: CognitoUserSession): void {
    const idToken = session.getIdToken().getJwtToken();
    
    // Configure AWS SDK with Cognito credentials
    AWS.config.region = config.aws.region;
    AWS.config.credentials = new AWS.CognitoIdentityCredentials({
      IdentityPoolId: config.aws.identityPoolId!,
      Logins: {
        [`cognito-idp.${config.aws.region}.amazonaws.com/${config.aws.userPoolId}`]: idToken
      }
    });

    // Refresh credentials
    (AWS.config.credentials as AWS.CognitoIdentityCredentials).refresh((error) => {
      if (error) {
        console.error('Access denied');
      }
    });
  }

  /**
   * Navigate to hosted UI for sign-in (if needed)
   */
  redirectToSignIn(): void {
    if (config.aws.hostedUIUrl) {
      window.location.href = `${config.aws.hostedUIUrl}/login?client_id=${config.aws.clientId}&response_type=code&scope=email+openid+profile&redirect_uri=${encodeURIComponent(window.location.origin)}`;
    } else {
      console.error('Not allowed to access.');
    }
  }
}

// Export singleton instance
export const authService = new AuthService();
